import { useAuthStore } from '../store/auth';

export default function Settings() {
  const { user } = useAuthStore();

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Settings</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage your account and platform settings.
          </p>
        </div>
      </div>

      {/* Profile Section */}
      <div className="mt-8">
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-[var(--border-color)]">
            <h3 className="text-lg font-medium leading-6 text-[var(--text-primary)]">
              Profile Information
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Name</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)]">{user?.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Email</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)]">{user?.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Role</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)] capitalize">
                  {user?.role?.replace('_', ' ')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--text-muted)]">Tenant ID</dt>
                <dd className="mt-1 text-sm text-[var(--text-primary)]">
                  {user?.tenantId || 'All Tenants (Super Admin)'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Platform Settings */}
      <div className="mt-8">
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-[var(--border-color)]">
            <h3 className="text-lg font-medium leading-6 text-[var(--text-primary)]">
              Platform Settings
            </h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Configure platform-wide settings.
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              {/* Payment Settings */}
              <div>
                <h4 className="text-sm font-medium text-[var(--text-secondary)]">Payment Methods</h4>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded border-[var(--border-color)] text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-[var(--text-secondary)]">
                      Cryptocurrency (USDT, BTC, ETH)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded border-[var(--border-color)] text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-[var(--text-secondary)]">PayPal</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded border-[var(--border-color)] text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-[var(--text-secondary)]">Bank Wire Transfer</span>
                  </label>
                </div>
              </div>

              {/* Minimum Payout */}
              <div>
                <label
                  htmlFor="minPayout"
                  className="block text-sm font-medium text-[var(--text-secondary)]"
                >
                  Minimum Payout Amount
                </label>
                <div className="mt-1 relative rounded-md shadow-sm max-w-xs">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-[var(--text-muted)] sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="minPayout"
                    defaultValue="100"
                    className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-12 sm:text-sm border-[var(--border-color)] rounded-md"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-[var(--text-muted)] sm:text-sm">USD</span>
                  </div>
                </div>
              </div>

              {/* Cookie Duration */}
              <div>
                <label
                  htmlFor="cookieDuration"
                  className="block text-sm font-medium text-[var(--text-secondary)]"
                >
                  Cookie Duration (days)
                </label>
                <input
                  type="number"
                  id="cookieDuration"
                  defaultValue="30"
                  className="mt-1 input max-w-xs"
                />
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  How long referral cookies are valid for tracking conversions.
                </p>
              </div>

              <div className="pt-4">
                <button type="button" className="btn btn-primary">
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div className="mt-8">
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-[var(--border-color)]">
            <h3 className="text-lg font-medium leading-6 text-[var(--text-primary)]">API Keys</h3>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Manage API keys for S2S postback integration.
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="bg-[var(--bg-secondary)] p-4 rounded-md">
              <h4 className="text-sm font-medium text-[var(--text-secondary)]">Postback URL Template</h4>
              <code className="mt-2 block text-xs bg-[var(--bg-tertiary)] p-2 rounded overflow-x-auto">
                {`https://api.example.com/api/postbacks?tenant={tenant_slug}&event={event_type}&click_id={click_id}&player_id={player_id}&amount={amount}`}
              </code>
            </div>

            <div className="mt-4">
              <p className="text-sm text-[var(--text-secondary)]">
                Available placeholders:
              </p>
              <ul className="mt-2 text-xs text-[var(--text-muted)] space-y-1">
                <li>
                  <code>{'{click_id}'}</code> - Original click tracking ID
                </li>
                <li>
                  <code>{'{player_id}'}</code> - Player/Trader ID from your system
                </li>
                <li>
                  <code>{'{event_type}'}</code> - registration, ftd, deposit, bet, win, trade
                </li>
                <li>
                  <code>{'{amount}'}</code> - Transaction amount
                </li>
                <li>
                  <code>{'{currency}'}</code> - Currency code (default: USD)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
